<template>
    <div :class="classes">
        <div class="drawer-accordion__head" @click="handleClick">
            <div class="drawer-accordion__caret-wrapper">
                <CompassIcon
                    icon="caret-right-fill"
                    size="extra-small"
                    class="drawer-accordion__caret"
                />
            </div>
            <CompassColoredIcon :icon="coloredIcon" :color="coloredIconColor" />

            <p class="drawer-accordion__title typographies-paragraph-caps-small-bold">
                {{ title }}
            </p>

            <CompassIcon
                v-if="props.hasError"
                class="drawer-accordion__error-icon"
                icon="error-circle-fill"
                :size="CompassEnums.IconSize.SMALL"
            />
            <slot name="action" />
        </div>

        <div ref="accordionContent" class="drawer-accordion__content" :style="contentMaxHeight">
            <div ref="accordionContentWrapper" class="drawer-accordion__content-wrapper">
                <slot />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick, useTemplateRef } from 'vue';
import LocalStorage from 'Core/commons/LocalStorage';

interface DrawerAccordionProps {
    title: string;
    id: string;
    openByDefault?: boolean;
    coloredIcon?: string;
    hasError?: boolean;
}

const props = withDefaults(defineProps<DrawerAccordionProps>(), {
    openByDefault: false,
    coloredIcon: 'pig-money',
    hasError: false,
});

const localstorage: InstanceType<typeof LocalStorage> = new LocalStorage('drawer', 'accordion');

const isOpen = ref<boolean>(true);
const contentHeight = ref<number>(0);
const observer = ref<ResizeObserver | null>(null);

const accordionContent = useTemplateRef<HTMLElement>('accordionContent');
const accordionContentWrapper = useTemplateRef<HTMLElement>('accordionContentWrapper');

const classes = computed<string[]>(() => [
    'drawer-accordion',
    isOpen.value ? 'drawer-accordion--open' : '',
    props.hasError ? 'drawer-accordion--error' : '',
]);

const coloredIconColor = computed<string>(() => {
    if (props.hasError) {
        return CompassEnums.ColoredIconColorType.RED;
    }
    return isOpen.value
        ? CompassEnums.ColoredIconColorType.BRAND
        : CompassEnums.ColoredIconColorType.GRAY;
});

const contentMaxHeight = computed<string>(
    () => `max-height: ${isOpen.value ? contentHeight.value : 0}px;`,
);

const setContentHeight = (): void => {
    if (accordionContent.value) {
        contentHeight.value = accordionContent.value.scrollHeight;
    }
};

const handleClick = (): void => {
    isOpen.value = !isOpen.value;
    // Accept any object, but only set boolean for our id
    const raw = localstorage.get();
    const storedValues: Record<string, unknown> = raw && typeof raw === 'object' ? raw : {};
    storedValues[props.id] = isOpen.value;
    localstorage.set({ ...storedValues });
};

const initObserver = (): void => {
    if (!accordionContentWrapper.value) return;
    const resizeObserver = new ResizeObserver(setContentHeight);
    resizeObserver.observe(accordionContentWrapper.value);
    observer.value = resizeObserver;
};

onMounted(() => {
    const storedValues = localstorage.get() as Record<string, boolean> | null;
    isOpen.value = storedValues?.[props.id] ?? props.openByDefault ?? true;
    initObserver();
    nextTick(() => {
        setContentHeight();
    });
});

onBeforeUnmount(() => {
    if (observer.value && accordionContentWrapper.value) {
        observer.value.unobserve(accordionContentWrapper.value);
    }
});
</script>

<style lang="scss" scoped>
.drawer-accordion {
    border-bottom: 1px solid var(--colors-common-border-lighter);

    &__head {
        align-items: center;
        cursor: pointer;
        display: flex;
        gap: var(--spacing-2);
        padding: var(--spacing-2);
        position: relative;
        transition: all 200ms ease-in-out;
        z-index: 1;

        &:hover {
            background: var(--colors-common-background-hover);
        }
    }

    &--open {
        .drawer-accordion__caret {
            transform: rotate(90deg);
        }

        .drawer-accordion__title {
            color: var(--colors-common-text-standard-active);
        }
    }

    &--error {
        .drawer-accordion__title {
            color: var(--colors-common-text-alert-default);
        }
    }

    &__error-icon {
        color: var(--colors-common-icon-alert);
    }

    &__caret {
        flex-shrink: 0;
        transition: all 200ms ease-in-out;

        &-wrapper {
            align-items: center;
            display: flex;
            flex-shrink: 0;
            height: var(--spacing-3);
            justify-content: center;
            width: var(--spacing-3);
        }
    }

    :deep(.compass-colored-icon) {
        flex-shrink: 0;
    }

    &__title {
        flex: 1;
        margin: 0;
        word-break: break-all;

        + * {
            flex-shrink: 0;
        }
    }

    &__content {
        max-height: 0;
        overflow: hidden;
        transition: all 200ms ease-in-out;

        &-wrapper {
            padding: var(--spacing-1) var(--spacing-2) var(--spacing-3) var(--spacing-2);
        }
    }
}
</style>
